/**
 * Assess whether pinning `target` relative to `current` is a "small" rollback
 * (one patch back or a pre-release of the current patch) or a "large" one
 * that warrants an extra warning in the pin confirmation dialog.
 */

export type PinDistance =
  | { kind: 'close' }
  | { kind: 'far'; reason: string }
  | { kind: 'unknown'; reason: string };

interface SemverParts {
  major: number;
  minor: number;
  patch: number;
  prerelease: string | null;
}

// Matches `0.13.2` and `0.13.3-daily.20260421.55` style pre-release.
const SEMVER_RE = /^(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?$/;

function parse(v: string): SemverParts | null {
  const m = SEMVER_RE.exec(v);
  if (!m) return null;
  return {
    major: Number(m[1]),
    minor: Number(m[2]),
    patch: Number(m[3]),
    prerelease: m[4] ?? null,
  };
}

/**
 * Classify pinning `target` relative to `current` (typically `latest.json`).
 *
 * - `close`: same major/minor; patch delta within 1 or `target` is a
 *   pre-release of `current.patch` / `current.patch + 1`.
 * - `far`: anything else — different major/minor, more than one patch back,
 *   or forward jumps of multiple patches.
 * - `unknown`: one of the versions doesn't parse as semver.
 */
export function classifyPin(target: string, current: string | null): PinDistance {
  if (!current) {
    return { kind: 'unknown', reason: 'Latest version is unknown.' };
  }
  const t = parse(target);
  const c = parse(current);
  if (!t) return { kind: 'unknown', reason: `Target ${target} is not semver.` };
  if (!c) return { kind: 'unknown', reason: `Latest ${current} is not semver.` };

  if (t.major !== c.major) {
    return {
      kind: 'far',
      reason: `Crossing a major version (${c.major}.x → ${t.major}.x).`,
    };
  }
  if (t.minor !== c.minor) {
    return {
      kind: 'far',
      reason: `Crossing a minor version (${c.major}.${c.minor}.x → ${t.major}.${t.minor}.x).`,
    };
  }

  const patchDelta = c.patch - t.patch;

  // Same patch or target is a pre-release of the same patch → close.
  if (t.patch === c.patch) return { kind: 'close' };

  // Exactly one patch back → close (typical rollback).
  if (patchDelta === 1) return { kind: 'close' };

  // Target is a pre-release of a patch up to one ahead of current → close.
  if (t.prerelease && t.patch === c.patch + 1) return { kind: 'close' };

  if (patchDelta > 1) {
    return {
      kind: 'far',
      reason: `Rolling back ${patchDelta} patches (${current} → ${target}).`,
    };
  }

  // Forward jump (target.patch > current.patch + 1).
  return {
    kind: 'far',
    reason: `Forward jump of ${-patchDelta} patches (${current} → ${target}).`,
  };
}
